/**
 * Действия режима «Основы».
 *
 * Занятие целиком — один прогон набора: цикл Memory Loop здесь не нужен,
 * пользователь ещё не знает клавиш, а не забыл их.
 */

import { useCallback } from 'react';
import type { TypingSession } from '@type2x/typing-engine';
import type { Workspace } from '@type2x/feature-personal-workspace';
import type { SessionPayload } from '@type2x/feature-sync';

import type { Runtime } from '../runtime.js';
import type { AppScreen } from '../state/app-state.js';
import { finishFundamentals, fundamentalsScreen } from '../state/fundamentals.js';

/** Действия режима, которые приложение передаёт экранам. */
export interface FundamentalsActions {
  readonly onStartFundamentals: () => void;
  readonly onFundamentalsDone: (run: TypingSession) => void;
}

/** Что нужно режиму от приложения. */
export interface FundamentalsDeps {
  readonly workspace: Workspace | null;
  readonly commit: (next: Workspace) => Promise<void>;
  readonly screen: AppScreen;
  readonly setScreen: (screen: AppScreen) => void;
  readonly submit: (payload: SessionPayload) => Promise<unknown>;
}

export function useFundamentals(runtime: Runtime, deps: FundamentalsDeps): FundamentalsActions {
  const { workspace, commit, screen, setScreen, submit } = deps;

  const onStartFundamentals = useCallback(() => {
    if (workspace === null) return;
    setScreen(fundamentalsScreen(runtime, workspace));
  }, [runtime, workspace, setScreen]);

  const onFundamentalsDone = useCallback(
    (run: TypingSession) => {
      if (workspace === null || screen.name !== 'fundamentals') return;

      const transition = finishFundamentals(runtime, workspace, screen.plan, run, screen.sessionId);
      void commit(transition.workspace);
      setScreen(transition.screen);

      // Отправка идёт после показа результата: сеть не задерживает экран.
      if (transition.sessionPayload !== undefined) {
        void submit(transition.sessionPayload);
      }
    },
    [runtime, workspace, screen, commit, setScreen, submit],
  );

  return { onStartFundamentals, onFundamentalsDone };
}
